import clsx from 'clsx';
import { Button } from 'common/components/Button';
import { IBlogItem } from 'common/types/blog';
import { IAnyType } from 'common/types/common';
import { normalizeLocalizedHref, normalizeLocalizedHtml } from 'common/utils/url';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { BlogFAQ } from '../BlogFAQ';

interface Props {
  blog: IBlogItem;
}

export const BlogContent: React.FC<Props> = ({ blog }) => {
  const { i18n } = useTranslation();
  const normalizeHtml = (value: string) => normalizeLocalizedHtml(value, i18n.language);

  const renderContent = (item: IAnyType, index: number) => {
    if (item.type === 'h2') {
      return (
        <h2
          key={`content-${index}`}
          className={clsx('heading-2 mb-3 mt-20 font-semibold lg:mb-6', item.className)}
          dangerouslySetInnerHTML={{ __html: normalizeHtml(item.value) }}
        />
      );
    }
    if (item.type === 'h3') {
      return (
        <h3
          key={`content-${index}`}
          className={clsx('heading-3 mb-3 mt-16 font-semibold', item.className)}
          dangerouslySetInnerHTML={{ __html: normalizeHtml(item.value) }}
        />
      );
    }
    if (item.type === 'h4') {
      return (
        <h4
          key={`content-${index}`}
          className={clsx('heading-4 mb-2 mt-8 font-semibold', item.className)}
          dangerouslySetInnerHTML={{ __html: normalizeHtml(item.value) }}
        />
      );
    }
    if (item.type === 'paragraph') {
      if (Array.isArray(item.value)) {
        return (
          <p key={`content-${index}`} className={clsx('mb-6 mt-2 max-w-full', item.className)}>
            {item.value.map((innerItem: IAnyType, innerItemIndex: number) => {
              if (innerItem.type === 'h3') {
                return (
                  <h3
                    key={`content-${innerItemIndex}`}
                    className={clsx('heading-3 mb-1 font-semibold', innerItem.className)}
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                  />
                );
              }
              if (innerItem.type === 'link') {
                const normalizedHref = normalizeLocalizedHref(innerItem.href, i18n.language);
                return (
                  <Link
                    key={`content-${innerItemIndex}`}
                    aria-label={innerItem.value}
                    className="text-primary"
                    dangerouslySetInnerHTML={{ __html: innerItem.value }}
                    href={normalizedHref}
                    target={innerItem.target || '_self'}
                    rel={innerItem.rel || ''}
                  />
                );
              }
              if (innerItem.type === 'text') {
                return (
                  <span
                    key={`content-${innerItemIndex}`}
                    dangerouslySetInnerHTML={{ __html: innerItem.value }}
                  />
                );
              }
              return <></>;
            })}
          </p>
        );
      }
      return (
        <p
          key={`content-${index}`}
          className={clsx('mb-6 mt-2 max-w-full', item.className)}
          dangerouslySetInnerHTML={{ __html: normalizeHtml(item.value) }}></p>
      );
    }
    if (item.type === 'ul') {
      return (
        <ul key={`content-${index}`} className={clsx('list-disc pl-4', item.className)}>
          {item.content.map((li: IAnyType, listIndex: number) => {
            if (Array.isArray(li.value)) {
              return (
                <li key={`list-${listIndex}`} className="mt-3 max-w-full">
                  {li.value.map((innerItem: IAnyType, innerItemIndex: number) => {
                    if (innerItem.type === 'h3') {
                      return (
                        <h3
                          key={`content-${innerItemIndex}`}
                          className={clsx('heading-3 mb-1 font-semibold', innerItem.className)}
                          dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                        />
                      );
                    }
                    if (innerItem.type === 'link') {
                      const normalizedHref = normalizeLocalizedHref(innerItem.href, i18n.language);
                      return (
                        <Link
                          key={`list-${innerItemIndex}`}
                          aria-label={innerItem.value}
                          className="text-primary"
                          href={normalizedHref}
                          dangerouslySetInnerHTML={{ __html: innerItem.value }}
                        />
                      );
                    }
                    if (innerItem.type === 'text') {
                      return (
                        <span
                          key={`list-${listIndex}`}
                          dangerouslySetInnerHTML={{ __html: innerItem.value }}
                        />
                      );
                    }
                    return <></>;
                  })}
                </li>
              );
            }
            return (
              <li
                key={`list-${listIndex}`}
                className="mt-3 max-w-full"
                dangerouslySetInnerHTML={{ __html: normalizeHtml(li.value) }}
              />
            );
          })}
        </ul>
      );
    }
    if (item.type === 'ol') {
      return (
        <ul key={`content-${index}`} className={clsx('list-decimal pl-4', item.className)}>
          {item.content.map((li: IAnyType, listIndex: number) => {
            if (Array.isArray(li.value)) {
              return (
                <li key={`list-${listIndex}`} className="mt-3 max-w-full">
                  {li.value.map((innerItem: IAnyType, innerItemIndex: number) => {
                    if (innerItem.type === 'h3') {
                      return (
                        <h3
                          key={`content-${innerItemIndex}`}
                          className={clsx('heading-3 mb-1 font-semibold', innerItem.className)}
                          dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                        />
                      );
                    }
                    if (innerItem.type === 'link') {
                      const normalizedHref = normalizeLocalizedHref(innerItem.href, i18n.language);
                      return (
                        <Link
                          key={`list-${innerItemIndex}`}
                          aria-label={innerItem.value}
                          className="text-primary"
                          href={normalizedHref}
                          dangerouslySetInnerHTML={{ __html: innerItem.value }}
                        />
                      );
                    }
                    if (innerItem.type === 'text') {
                      return (
                        <span
                          key={`list-${listIndex}`}
                          dangerouslySetInnerHTML={{ __html: innerItem.value }}
                        />
                      );
                    }
                    return <></>;
                  })}
                </li>
              );
            }
            return (
              <li
                key={`list-${listIndex}`}
                className="mt-3 max-w-full"
                dangerouslySetInnerHTML={{ __html: normalizeHtml(li.value) }}
              />
            );
          })}
        </ul>
      );
    }
    if (item.type === 'faq') {
      return <BlogFAQ key={`content-${index}`} content={item.content} />;
    }
    if (item.type === 'centered') {
      if (item.content?.length) {
        return (
          <div
            key={`content-${index}`}
            className={clsx(
              'work-heading mb-16 mt-20 max-w-full border border-[#080711] bg-white/5  px-8 py-12 text-center drop-shadow-xl md:px-8 lg:px-12',
              item.className,
            )}>
            {item.content.map((innerItem: IAnyType, innerItemIndex: number) => {
              if (innerItem.type === 'h2') {
                return (
                  <h2
                    key={`content-${innerItemIndex}`}
                    className={clsx('heading-2 mb-1 font-semibold', innerItem.className)}
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                  />
                );
              }
              if (innerItem.type === 'h3') {
                return (
                  <h3
                    key={`content-${innerItemIndex}`}
                    className={clsx('heading-3 mb-1 font-semibold', innerItem.className)}
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                  />
                );
              }
              if (innerItem.type === 'link') {
                const normalizedHref = normalizeLocalizedHref(innerItem.href, i18n.language);
                return (
                  <Link
                    key={`content-${innerItemIndex}`}
                    aria-label={innerItem.value}
                    className="text-primary"
                    href={normalizedHref}
                    dangerouslySetInnerHTML={{ __html: innerItem.value }}
                  />
                );
              }
              if (innerItem.type === 'text') {
                return (
                  <span
                    key={`content-${innerItemIndex}`}
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                  />
                );
              }
              return <></>;
            })}
          </div>
        );
      }
      return (
        <p
          key={`content-${index}`}
          className={clsx(
            'work-heading mb-16 mt-20 max-w-full border border-[#080711] bg-white/5  px-8 py-12 text-center drop-shadow-xl md:px-8 lg:px-12',
            item.className,
          )}
          dangerouslySetInnerHTML={{ __html: normalizeHtml(item.value) }}
        />
      );
    }
    if (item.type === 'button') {
      return (
        <div
          key={`content-${index}`}
          className={clsx('flex items-center justify-center text-center', item.className)}>
          <Button
            type="primary"
            href={normalizeLocalizedHref(item.href, i18n.language)}
            rel={item.rel || ''}>
            {item.value}
          </Button>
        </div>
      );
    }
    return <></>;
  };

  if (!blog.content?.length) {
    return <></>;
  }
  return (
    <section className="container border-b-[1px] border-b-gray-200/10">
      <div className="mx-auto max-w-full px-5 pb-20 pt-32 text-xl leading-[1.8rem] sm:max-w-[560px] md:max-w-[680px] lg:max-w-[900px] lg:pb-32 lg:pt-40 lg:text-2xl lg:leading-[2.2rem] xl:max-w-[1060px]">
        {blog.content.map(renderContent)}
      </div>
    </section>
  );
};
